import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import { blogService } from "../../services/blogService";
import { CreateBlogPostPayload, UpdateBlogPostPayload } from "../../types/blog";

export const useAuthorPostsQuery = () => {
  return useQuery({
    queryKey: queryKeys.blog.list(),
    queryFn: ({ signal }) => blogService.getPosts(signal),
  });
};

export const usePostDetailQuery = (id?: number | string) => {
  return useQuery({
    queryKey: queryKeys.blog.detail(id || ""),
    queryFn: ({ signal }) => blogService.getPostById(id!, signal),
    enabled: Boolean(id),
  });
};

export const useCategoriesQuery = () => {
  return useQuery({
    queryKey: queryKeys.blog.categories(),
    queryFn: ({ signal }) => blogService.getCategories(signal),
  });
};

export const useTagsQuery = () => {
  return useQuery({
    queryKey: queryKeys.blog.tags(),
    queryFn: ({ signal }) => blogService.getTags(signal),
  });
};

export const useCreatePostMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBlogPostPayload) => blogService.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
    },
  });
};

export const useUpdatePostMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateBlogPostPayload) => blogService.updatePost(data),
    onSuccess: (updatedPost) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
      if (updatedPost?.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.blog.detail(updatedPost.id),
        });
      }
      if (updatedPost?.slug) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.blog.detail(updatedPost.slug),
        });
      }
    },
  });
};

export const useDeletePostMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => blogService.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
    },
  });
};
